import { useViewMode, ViewMode } from '@ais-ui/custom-hooks';
import {
  ACTIVETAB,
  CENTERCOMPONENTS,
  RIGHTSIDECOMPONENTS,
  useMapPanelContext,
} from '../Map/MapPanel';
import { useReportContext } from '../Reports/ReportContext';
import { FocusEventDetail, useFocusEventListener } from './focusEvent';
import { useGotoEvent } from './useGotoEvent';

/**
 * Bridges a `focusEvent` request (dispatched from the event toast) into the map
 * UI: it ensures the Events panel is open — never toggling it closed — and
 * records the requested `focusedEventId` so the events list can expand and
 * scroll to that event once it renders.
 *
 * Mounted once inside the map subtree, where `MapPanelContext` is available.
 */
export const useFocusEventNavigation = (): void => {
  const {
    activeTab,
    navigateToTab,
    setRightSideComponent,
    centerComponent,
    setCenterComponent,
    setCenterComponentData,
    setTabChange,
    setTableExpanded,
    setFocusedEventId,
  } = useMapPanelContext();
  const { viewMode, setViewMode } = useViewMode();
  const { resetReportContext } = useReportContext();
  const gotoEvent = useGotoEvent();

  useFocusEventListener(({ eventId }: FocusEventDetail) => {
    // Mirror the sidebar "open Events" preparation so the panel lands in a
    // consistent state, but only ever open the tab (never toggle it closed).
    if (
      centerComponent === CENTERCOMPONENTS.SHIPTABLE ||
      centerComponent === CENTERCOMPONENTS.ROUTES
    ) {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    }
    setCenterComponentData(null);
    setTabChange(true);
    resetReportContext();
    setTableExpanded(true);

    if (viewMode === ViewMode.Detail) {
      setViewMode(ViewMode.Normal);
    }

    if (activeTab !== ACTIVETAB.EVENTS) {
      setRightSideComponent(RIGHTSIDECOMPONENTS.EVENTS);
      navigateToTab(ACTIVETAB.EVENTS);
    }

    setFocusedEventId(eventId);

    // Pan/zoom the map to the event location (same as the "Show event" action).
    gotoEvent(eventId).catch((error) => {
      console.error('Failed to navigate map to event', eventId, error);
    });
  });
};
